import React from 'react';

/**
 * DesignationIcon - Consistent SVG icons for place designations
 *
 * Replaces emoji designations with proper SVG icons per visual-assets-guide.md:
 * - Gem: Diamond outline (--gem purple)
 * - Highly Recommended: Thumbs up (--primary green)
 * - Visited: Checkmark circle (--text-secondary gray)
 * - Avoid: X circle (--error red)
 * - Want to Go: Bookmark outline (--gold)
 * - Notes: Pencil/document icon (--text-secondary)
 * - Unrated: Empty circle (--text-muted)
 */

// SVG path definitions for each designation
const ICON_PATHS = {
  gem: (
    // Diamond shape
    <>
      <path d="M6 3h12l4 6-10 12L2 9z" />
      <path d="M2 9h20" />
      <path d="M12 21V9" />
      <path d="M6 3l6 6 6-6" />
    </>
  ),
  'highly-recommended': (
    // Thumbs up
    <>
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </>
  ),
  visited: (
    // Checkmark circle
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  avoid: (
    // X circle
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6" />
      <path d="M9 9l6 6" />
    </>
  ),
  'want-to-go': (
    // Bookmark outline
    <>
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </>
  ),
  notes: (
    // Document with pencil
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </>
  ),
  unrated: (
    // Empty circle (r="10" to match visited/avoid circle size)
    <>
      <circle cx="12" cy="12" r="10" />
    </>
  ),
  saved: (
    // Pin/marker
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
};

// Color classes for each designation (uses CSS variables)
const DESIGNATION_COLORS = {
  gem: 'var(--gem)',
  'highly-recommended': 'var(--primary)',
  visited: 'var(--text-secondary)',
  avoid: 'var(--error)',
  'want-to-go': 'var(--gold)',
  notes: 'var(--text-secondary)',
  unrated: 'var(--text-muted)',
  saved: 'var(--text-secondary)',
};

// Labels for each designation
const DESIGNATION_LABELS = {
  gem: 'Gem',
  'highly-recommended': 'Highly Recommended',
  visited: 'Visited',
  avoid: 'Avoid',
  'want-to-go': 'Want to Go',
  notes: 'Notes',
  unrated: 'Unrated',
  saved: 'Saved',
};

/**
 * DesignationIcon Component
 *
 * @param {string} designation - One of: gem, highly-recommended, visited, avoid, want-to-go, notes, unrated, saved
 * @param {number} size - Icon size in pixels (default: 16)
 * @param {string} className - Additional CSS classes
 * @param {boolean} showLabel - Whether to show the label text alongside the icon
 * @param {string} color - Override color (uses CSS variable by default)
 * @param {number} strokeWidth - SVG stroke width (default: 2)
 * @param {object} style - Additional inline styles
 */
function DesignationIcon({
  designation,
  size = 16,
  className = '',
  showLabel = false,
  color,
  strokeWidth = 2,
  style = {},
}) {
  const paths = ICON_PATHS[designation] || ICON_PATHS.saved;
  const defaultColor = DESIGNATION_COLORS[designation] || DESIGNATION_COLORS.saved;
  const label = DESIGNATION_LABELS[designation] || 'Saved';
  const iconColor = color || defaultColor;

  const icon = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`designation-icon designation-icon-${designation} ${className}`}
      style={style}
    >
      {paths}
    </svg>
  );

  if (showLabel) {
    return (
      <span className="designation-icon-with-label" style={{ color: iconColor }}>
        {icon}
        <span className="designation-label">{label}</span>
      </span>
    );
  }

  return icon;
}

/**
 * Get designation icon as a function (for use in non-React contexts like maps)
 * Returns an SVG string
 */
export function getDesignationIconSvg(designation, size = 16, color) {
  const paths = {
    gem: '<path d="M6 3h12l4 6-10 12L2 9z"/><path d="M2 9h20"/><path d="M12 21V9"/><path d="M6 3l6 6 6-6"/>',
    'highly-recommended': '<path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>',
    visited: '<circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>',
    avoid: '<circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6"/><path d="M9 9l6 6"/>',
    'want-to-go': '<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>',
    notes: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/>',
    unrated: '<circle cx="12" cy="12" r="10"/>',
    saved: '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
  };

  const colors = {
    gem: '#6a1b9a',
    'highly-recommended': '#2d5a47',
    visited: '#666666',
    avoid: '#dc3545',
    'want-to-go': '#c9a961',
    notes: '#666666',
    unrated: '#aaaaaa',
    saved: '#666666',
  };

  const pathContent = paths[designation] || paths.saved;
  const iconColor = color || colors[designation] || colors.saved;

  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${pathContent}</svg>`;
}

/**
 * Get designation label
 */
export function getDesignationLabel(designation) {
  return DESIGNATION_LABELS[designation] || 'Saved';
}

/**
 * Get designation color CSS variable
 */
export function getDesignationColor(designation) {
  return DESIGNATION_COLORS[designation] || DESIGNATION_COLORS.saved;
}

export default DesignationIcon;
